import { Check, X } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { GoldButton } from "@/components/common/Buttons";
import { CRITERIA, PHILOSOPHY, IMAGES } from "@/lib/content";
import { useSeo } from "@/lib/seo";

const COMPARE = {
  us: ["Logique industrielle et entrepreneuriale", "Préservation de l'identité et des équipes", "Vision de long terme", "Synergies entre métiers complémentaires", "Proximité et connaissance du terrain"],
  them: ["Logique purement financière", "Restructurations et effacement de l'identité", "Horizon de revente court terme", "Portefeuille sans cohérence métier", "Distance avec la réalité opérationnelle"],
};

export default function Strategie() {
  useSeo({
    title: "Notre Stratégie — Acquérir des savoir-faire | Losta Group",
    description:
      "La stratégie de Losta Group : acquérir des savoir-faire, préserver les équipes et développer les entreprises par les synergies. Une holding industrielle, pas un fonds financier froid.",
    path: "/strategie",
  });
  return (
    <>
      <PageHero
        eyebrow="Notre Stratégie"
        lines={["Nous recherchons", "des savoir-faire,", "pas seulement des entreprises."]}
        sub="Chaque acquisition s'inscrit dans une logique de continuité, de synergies et de développement durable."
        image={IMAGES.workshop}
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading eyebrow="Les 5 critères" lines={["Ce que nous regardons", "dans une entreprise."]} className="mb-14" />
          <div className="divide-y divide-white/10 border-y border-white/10">
            {CRITERIA.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="group grid grid-cols-1 md:grid-cols-[auto_1fr_2fr] gap-4 md:gap-10 items-baseline py-7 px-2 -mx-2 rounded-lg hover:bg-white/[0.02] transition-colors">
                  <span className="font-mono text-sm text-gold">{c.n}</span>
                  <h3 className="font-serif text-2xl sm:text-3xl text-white group-hover:text-gold transition-colors">{c.title}</h3>
                  <p className="text-base text-[var(--txt-2)] font-light leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">Aucun critère financier précis n'est affiché tant qu'il n'a pas été défini.</p>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#0B0D13] border-y border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading center eyebrow="Une autre approche" lines={["Une holding industrielle,", "pas un fonds froid."]} className="mb-14" />
          <div className="grid gap-5 lg:grid-cols-2">
            <Reveal>
              <div className="rounded-2xl border border-[var(--gold)]/30 bg-[var(--gold)]/[0.04] p-8 h-full">
                <div className="font-serif text-2xl text-gold mb-5">Losta Group</div>
                <ul className="space-y-3">
                  {COMPARE.us.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="rounded-2xl border border-white/10 card-lux p-8 h-full">
                <div className="font-serif text-2xl text-slate-400 mb-5">Un fonds classique</div>
                <ul className="space-y-3">
                  {COMPARE.them.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-400"><X className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />{t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionHeading center eyebrow="Notre modèle" lines={["Un accompagnement", "sur-mesure."]} className="mb-14" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-white/10 card-lux p-6">
                  <span className="font-mono text-xs text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-2xl text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-[var(--txt-2)] font-light">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 text-center"><GoldButton to="/ceder-votre-entreprise" testid="strategie-cta">Parler de mon entreprise</GoldButton></div>
        </div>
      </section>
    </>
  );
}
